<script setup>
import { computed } from 'vue';
import { messageTimestamp } from 'shared/helpers/timeHelper';

import MessageStatus from './MessageStatus.vue';
import Icon from 'next/icon/Icon.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMapGetter } from 'dashboard/composables/store';
import { useMessageContext } from './provider.js';
import { isMessagePinned } from 'dashboard/helper/pinnedMessages';
import { isGroupConversation } from './groupParticipant.js';
import {
  isPastConfirmationWindow,
  useUnconfirmedClock,
} from './unconfirmedGroupSend.js';

import { MESSAGE_STATUS, MESSAGE_TYPES } from './constants';

defineProps({
  statusOnMedia: { type: Boolean, default: false },
});

const {
  inbox,
  isAFacebookInbox,
  isALineChannel,
  isAPIInbox,
  isASmsInbox,
  isATelegramChannel,
  isATwilioChannel,
  isAWebWidgetInbox,
  isAWhatsAppChannel,
  isAnEmailChannel,
  isAnInstagramChannel,
  isATiktokChannel,
} = useInbox();

const isWameInbox = computed(() => {
  const attrs =
    inbox.value?.additionalAttributes ||
    inbox.value?.additional_attributes ||
    inbox.value?.channel?.additionalAttributes ||
    inbox.value?.channel?.additional_attributes ||
    {};

  return (
    isAPIInbox.value &&
    (attrs.aceleresOutboundMode ?? attrs.aceleres_outbound_mode) ===
      'webhook' &&
    Boolean(attrs.aceleresInstanceId ?? attrs.aceleres_instance_id)
  );
});

const {
  status,
  id,
  isPrivate,
  createdAt,
  sourceId,
  messageType,
  contentAttributes,
  conversationId,
} = useMessageContext();

// Aceleres 2026-09-01: alfinete ao lado do horario quando a mensagem esta entre
// as fixadas da conversa. A lista sai do helper `pinnedMessages` — a mesma
// fonte que o banner do topo e o menu de contexto leem, para os tres nunca
// discordarem.
const conversationGetter = useMapGetter('getConversationById');
const isPinnedMessage = computed(() =>
  isMessagePinned(conversationGetter.value(conversationId.value), id.value)
);

const uiSettings = useMapGetter('getUISettings');
const isStarred = computed(() => {
  const starredIds = uiSettings.value?.starred_message_ids || [];
  return starredIds.some(messageId => Number(messageId) === Number(id.value));
});

const readableTime = computed(() => messageTimestamp(createdAt.value, 'HH:mm'));

// Aceleres: badge "editada" ao lado do horário quando o content foi editado
// (marcado por `content_attributes.edited` no patch_content do backend).
const showStatusIndicator = computed(() => {
  if (isPrivate.value) return false;
  // Don't show status for failed messages, we already show error message
  if (status.value === MESSAGE_STATUS.FAILED) return false;
  // Don't show status for deleted messages
  if (contentAttributes.value?.deleted) return false;

  if (messageType.value === MESSAGE_TYPES.OUTGOING) return true;
  if (messageType.value === MESSAGE_TYPES.TEMPLATE) return true;

  return false;
});

const isSent = computed(() => {
  if (!showStatusIndicator.value) return false;

  // Messages will be marked as sent for the Email channel if they have a source ID.
  if (isAnEmailChannel.value) return !!sourceId.value;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isASmsInbox.value ||
    isATelegramChannel.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.SENT;
  }

  // WAME starts the Chatwoot message as `sent`, before the provider returns
  // its external id. Until then, show progress instead of a false tick.
  if (isWameInbox.value) {
    return !!sourceId.value && status.value === MESSAGE_STATUS.SENT;
  }

  // API inbox messages use real sent/delivered/read status values from the external system.
  if (isAPIInbox.value) return status.value === MESSAGE_STATUS.SENT;

  // All messages will be mark as sent for the Line channel, as there is no source ID.
  if (isALineChannel.value) return true;

  return false;
});

const isDelivered = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isASmsInbox.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.DELIVERED;
  }
  if (isWameInbox.value) {
    return !!sourceId.value && status.value === MESSAGE_STATUS.DELIVERED;
  }

  // API inbox messages use real delivered status from the external system.
  if (isAPIInbox.value) return status.value === MESSAGE_STATUS.DELIVERED;
  // All messages marked as delivered for the web widget inbox once they are sent.
  if (isAWebWidgetInbox.value) {
    return status.value === MESSAGE_STATUS.SENT;
  }
  if (isALineChannel.value) {
    return status.value === MESSAGE_STATUS.DELIVERED;
  }

  return false;
});

const isRead = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.READ;
  }

  if (isWameInbox.value) {
    return !!sourceId.value && status.value === MESSAGE_STATUS.READ;
  }

  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return status.value === MESSAGE_STATUS.READ;
  }

  return false;
});

// Aceleres 09/09/2026: o relogio nao pode ser o destino de "nao sei".
//
// Todos os ramos de isRead/isDelivered/isSent sao decididos pelo tipo do canal,
// e o tipo vem de `inboxes/getInboxById`. Quando a inbox nao esta no store o
// getter devolve undefined, nenhum ramo casa, e a mensagem cai aqui como se
// ainda estivesse a sair — para sempre.
//
// Isso nao e hipotetico: o agente pode ser o RESPONSAVEL de uma conversa numa
// inbox de que nao e membro (medido na conversa 27 da conta 2569 — a inbox 450
// nao vem em /api/v1/accounts/2569/inboxes para esse agente). A conversa abre,
// a mensagem sai e e entregue, e a bolha mostra relogio indefinidamente.
//
// Sem canal, o status do servidor e a melhor informacao que existe: e ele que
// diz sent/delivered/read. Mostra-lo e pior que um tique especifico por canal,
// mas e verdade — e o relogio permanente e mentira.
const statusToShow = computed(() => {
  if (isRead.value) return MESSAGE_STATUS.READ;
  if (isDelivered.value) return MESSAGE_STATUS.DELIVERED;
  if (isSent.value) return MESSAGE_STATUS.SENT;

  // O sinal e a ausencia de `channelType`, nao de `inbox`: o getter
  // `inboxes/getInboxById` devolve `{}` (truthy) para um id que nao esta no
  // store, entao testar o objeto nunca acusaria nada.
  if (!inbox.value?.channelType && status.value) return status.value;

  return MESSAGE_STATUS.PROGRESS;
});

// Aceleres 2026-09-14: envio para grupo sem recibo do WhatsApp depois de 3 min
// (ver unconfirmedGroupSend.js). Só troca o tique; a mensagem continua lá.
const clock = useUnconfirmedClock();
const isUnconfirmedGroupSend = computed(
  () =>
    statusToShow.value === MESSAGE_STATUS.SENT &&
    messageType.value === MESSAGE_TYPES.OUTGOING &&
    isGroupConversation(conversationGetter.value(conversationId.value)) &&
    isPastConfirmationWindow(createdAt.value, clock.value)
);
</script>

<template>
  <div
    class="flex items-center gap-1 text-[13px] leading-4"
    :class="{ 'acl-message-meta--starred': isStarred }"
  >
    <!-- Aceleres: a badge "editada" ficava aqui, mas o meta é absoluto no
         canto do bubble e ela vazava pra fora quando a mensagem era curta.
         Agora ela é renderizada dentro do próprio conteúdo (ver Text/Index.vue),
         então flui com o texto e o bubble cresce naturalmente. -->
    <Icon
      v-if="isStarred"
      icon="i-lucide-star"
      class="size-3 shrink-0 fill-current"
      :aria-label="$t('CONVERSATION.CONTEXT_MENU.STARRED')"
    />
    <Icon
      v-if="isPinnedMessage"
      icon="i-lucide-pin"
      class="size-3 fill-current"
      aria-label="Mensagem fixada"
    />
    <div class="inline">
      <time class="inline">{{ readableTime }}</time>
    </div>
    <Icon v-if="isPrivate" icon="i-lucide-lock-keyhole" class="size-3" />
    <Icon
      v-if="showStatusIndicator && isUnconfirmedGroupSend"
      v-tooltip.top-start="$t('CHAT_LIST.SENT_UNCONFIRMED')"
      icon="i-lucide-triangle-alert"
      class="size-3.5 shrink-0"
      :class="statusOnMedia ? 'text-white' : 'text-n-amber-10'"
      data-testid="message-unconfirmed-group-send"
      :aria-label="$t('CHAT_LIST.SENT_UNCONFIRMED')"
    />
    <MessageStatus
      v-else-if="showStatusIndicator"
      :status="statusToShow"
      :on-media="statusOnMedia"
    />
  </div>
</template>
